import { Device } from "@fairfleet/geotab";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import Alert from "@mui/material/Alert";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { endOfDay } from "date-fns/endOfDay";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EnergyReportGrid } from "./components/EnergyReportGrid";
import { QualitySummary } from "./components/QualitySummary";
import { CONSUMPTION_HIDDEN_COLUMNS, useConsumptionColumns } from "./hooks/useConsumptionColumns";
import { useEnergyReport } from "./hooks/useEnergyReport";
import { useEventAddresses } from "./hooks/useEventAddresses";
import { useChargeEventColumns, useFuelUpEventColumns } from "./hooks/useEventColumns";
import { useVinDecoded } from "./hooks/useVinDecoded";
import { mergeChargeSessions } from "./models/chargeEventRow";
import { countByStatus } from "./models/countByStatus";
import { devicesActiveDuring } from "./models/devicesActiveDuring";
import { EnergyDevice } from "./models/energyDevice";
import {
  CHARGE_EVENT_FRACTION_DIGITS,
  CONSUMPTION_FRACTION_DIGITS,
  FUEL_UP_EVENT_FRACTION_DIGITS,
} from "./models/exportFractionDigits";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { DateRangeSelect } from "@/components/input/DateRangeSelect";
import { RESULTS_LIMIT_DEVICE } from "@/constants/resultsLimits";
import { MAX_SCAN_VEHICLES } from "@/constants/scanLimits";
import { useDevicesBySearchWithNumDevices } from "@/hooks/devices/useDevicesBySearch";
import getDeviceLicensePlate from "@/hooks/getDeviceLicensePlate";
import { formatDate } from "@/hooks/metrics/useFormattedDate";
import { useDriverNamesByIds } from "@/hooks/users/useDriverNamesByIds";
import { usePersistedDateRange } from "@/hooks/userState/usePersistedDateRange";
import { Report } from "@/utilities/export/report";

type TabKey = "consumption" | "chargeEvents" | "fuelUpEvents";

/** What a started read covers, fixed when the user pressed start. */
interface ReportRun {
  sessionId: string | null;
  devices: EnergyDevice[];
  /** The vehicles behind `devices`, so the VIN columns describe the vehicles that were read. */
  sourceDevices: Device[];
  fromDate: Date;
  toDate: Date;
  filter: string | undefined;
  stopped: boolean;
}

const NO_DEVICES: EnergyDevice[] = [];
const NO_SOURCE_DEVICES: Device[] = [];

/** The "select all" line at the top of the vehicle picker; never part of the selection. */
const SELECT_ALL = { id: "__selectAll__" } as Device;

/** Up to this many picked vehicles show as chips; more show as one count. */
const MAX_VEHICLE_CHIPS = 2;

const filterDevices = createFilterOptions<Device>({
  stringify: device =>
    `${device.name ?? ""} ${getDeviceLicensePlate(device)} ${device.serialNumber ?? ""}`,
});

// The "select all" line stays on top whatever is typed.
const deviceFilterOptions: typeof filterDevices = (options, state) => [
  SELECT_ALL,
  ...filterDevices(
    options.filter(option => option !== SELECT_ALL),
    state,
  ),
];

function deviceLabel(device: Device): string {
  const plate = getDeviceLicensePlate(device);
  return plate ? `${device.name ?? ""} (${plate})` : (device.name ?? "");
}

export default function EnergyConsumptionReportPage() {
  const { t, i18n } = useTranslation();
  const { fromDate, toDate, setRange, isReady } = usePersistedDateRange({
    key: "dateRange:energyReport",
    defaultLookbackDays: 30,
  });

  // Every listed vehicle is picked until the user takes one out, so only the ones taken out are
  // kept: a narrower group filter or another period changes the list, and the new list then starts
  // out fully picked without anything having to reset the selection.
  const [deselectedIds, setDeselectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [activeTab, setActiveTab] = useState<TabKey>("consumption");
  // On by default, matching the report built into Geotab, which presents a plug-in session as one
  // row. The ungrouped list stays one click away because the grouping has to be inferred.
  const [mergeSessions, setMergeSessions] = useState(true);

  const allDevicesQuery = useDevicesBySearchWithNumDevices(
    "",
    true,
    RESULTS_LIMIT_DEVICE,
    undefined,
    // A refetch would rebuild the device list and re-run the fleet-wide merge every 30 s.
    false,
  );
  const allDevices = useMemo(
    () => devicesActiveDuring(allDevicesQuery.data?.devices ?? [], fromDate),
    [allDevicesQuery.data, fromDate],
  );

  const selectedDevices = useMemo(
    () => allDevices.filter(device => !deselectedIds.has(device.id)),
    [allDevices, deselectedIds],
  );
  const allSelected = selectedDevices.length === allDevices.length;

  const devices = useMemo(
    () =>
      selectedDevices.map(device => ({
        id: device.id,
        name: device.name ?? "",
        licensePlate: getDeviceLicensePlate(device),
      })),
    [selectedDevices],
  );

  // Started by hand rather than on open: every vehicle costs at least six calls from the budget
  // the whole add-in shares, so the read covers a fixed set and period, and changing the filter
  // or the dates afterwards does not start it again.
  const { activeSessionId } = useGeotabClient();
  const [storedRun, setRun] = useState<ReportRun | null>(null);
  // A run belongs to the database it was started on: after a session switch its vehicle ids mean
  // nothing, and keeping it would read them again on the new database without a click. Switching
  // back resumes an unfinished run; its vehicles already read come from the cache.
  const run = storedRun?.sessionId === activeSessionId ? storedRun : null;
  const tooManyVehicles = devices.length > MAX_SCAN_VEHICLES;
  // A capped device list holds only part of the fleet, so with every listed vehicle picked the
  // count of `devices` is a lower bound and must not be printed as the size of the selection.
  const deviceListTruncated = (allDevicesQuery.data?.total ?? 0) >= RESULTS_LIMIT_DEVICE;
  const selectionCountIsExact = !(deviceListTruncated && allSelected);
  // The grid keeps showing the run while the picker and the vehicle filter move on; say so, so
  // its figures are not read as belonging to the period now shown in the picker.
  const isStale =
    run !== null &&
    (run.fromDate.getTime() !== fromDate.getTime() ||
      run.toDate.getTime() !== toDate.getTime() ||
      run.devices.map(device => device.id).join() !== devices.map(device => device.id).join());
  const report = useEnergyReport(
    run?.devices ?? NO_DEVICES,
    run?.fromDate ?? fromDate,
    run?.toDate ?? toDate,
    run !== null && !run.stopped,
    run?.stopped ?? false,
  );
  const isRunning = run !== null && !run.stopped && report.progress.isLoading;
  const isComplete = run !== null && !report.progress.isLoading;
  const exportDisabled = !isComplete || isStale;

  const start = () =>
    setRun({
      sessionId: activeSessionId,
      devices,
      sourceDevices: selectedDevices,
      fromDate,
      toDate,
      // The whole list is what the group filter says; only a hand-picked set needs naming.
      filter: allSelected ? undefined : selectedDevices.map(device => device.name).join(", "),
      stopped: false,
    });
  const stop = () => {
    setRun(current => current && { ...current, stopped: true });
    report.cancel();
  };

  // Nothing is decoded before the start: the VIN columns describe the vehicles that were read,
  // and a fleet-wide list would cost one DecodeVinsNew call per 500 vehicles for an empty grid.
  const { byDeviceId: vinDecodedByDeviceId, error: vinError } = useVinDecoded(
    run?.sourceDevices ?? NO_SOURCE_DEVICES,
  );

  // Grouping is a bucket-and-sort over every charge event in the fleet, and the row list gets a
  // new identity each time a vehicle arrives — so left ungated it would re-sort the whole fleet
  // hundreds of times over, including while a tab that never shows the result is open.
  const showChargeEvents = activeTab === "chargeEvents";
  const chargeEventRows = useMemo(
    () =>
      showChargeEvents && mergeSessions
        ? mergeChargeSessions(report.chargeEventRows)
        : report.chargeEventRows,
    [report.chargeEventRows, mergeSessions, showChargeEvents],
  );

  // Only for the view being looked at, and only once the fleet is read: a fleet-wide period holds
  // thousands of positions, and every vehicle that arrives changes the set of positions to look
  // up. Geocoding while that happens would start a new round of the slowest call there is for
  // each arrival; afterwards it runs once over the final set.
  const geocodeReady = isComplete;
  const { addresses: chargeAddresses, error: chargeAddressError } = useEventAddresses(
    chargeEventRows,
    showChargeEvents && geocodeReady,
  );
  const { addresses: fuelUpAddresses, error: fuelUpAddressError } = useEventAddresses(
    report.fuelUpEventRows,
    activeTab === "fuelUpEvents" && geocodeReady,
  );
  const addressError = chargeAddressError ?? fuelUpAddressError;

  // Trips and fuel transactions reference a driver by id and nothing else, so the names are looked
  // up by the ids the read events carry, not from the group-scoped driver list (a vehicle of the
  // picked depot can be driven by someone whose groups lie elsewhere). Only once the fleet is read:
  // every arriving vehicle would change the id list and re-issue the lookup.
  const eventDriverIds = useMemo(
    () =>
      isComplete
        ? [...report.chargeEventRows, ...report.fuelUpEventRows].map(row => row.driverId)
        : undefined,
    [isComplete, report.chargeEventRows, report.fuelUpEventRows],
  );
  const {
    names: resolvedDriverNames,
    isLoading: driversLoading,
    isError: driversFailed,
    error: driversError,
  } = useDriverNamesByIds(eventDriverIds);
  // No names (blank cells) until they are known, rather than "unknown driver" for everyone.
  const driverNames = isComplete && !driversLoading ? resolvedDriverNames : undefined;

  const consumptionColumns = useConsumptionColumns(vinDecodedByDeviceId);
  const chargeEventColumns = useChargeEventColumns({
    vinDecodedByDeviceId,
    driverNames,
    addresses: chargeAddresses,
  });
  const fuelUpEventColumns = useFuelUpEventColumns({
    vinDecodedByDeviceId,
    driverNames,
    addresses: fuelUpAddresses,
  });

  const counts = useMemo(() => countByStatus(report.consumptionRows), [report.consumptionRows]);

  const exportReport = (title: string): Omit<Report, "table"> => ({
    title,
    // The period and vehicles the figures came from, not the current settings.
    period: { from: run?.fromDate ?? fromDate, to: run?.toDate ?? toDate },
    filter: run?.filter,
    extra: [
      [
        t("energyReport.export.completeness"),
        `${counts.ok} / ${counts.partial} / ${counts.missing + counts.failed}`,
      ],
    ],
  });

  return (
    <Stack spacing={2} sx={{ p: { xs: 1, md: 2 }, height: "100%" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
        <Autocomplete
          multiple
          size="small"
          options={allDevices}
          value={selectedDevices}
          onChange={(_event, value, _reason, details) => {
            if (details?.option === SELECT_ALL) {
              setDeselectedIds(
                allSelected ? new Set(allDevices.map(device => device.id)) : new Set(),
              );
              return;
            }
            const picked = new Set(value.map(device => device.id));
            setDeselectedIds(
              new Set(allDevices.filter(device => !picked.has(device.id)).map(device => device.id)),
            );
          }}
          filterOptions={deviceFilterOptions}
          disableCloseOnSelect
          getOptionLabel={device =>
            device === SELECT_ALL ? t("energyReport.selectAll") : deviceLabel(device)
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, device, { selected }) => {
            const { key, ...optionProps } = props;
            const isSelectAll = device === SELECT_ALL;
            return (
              <li key={key} {...optionProps}>
                <Checkbox
                  size="small"
                  sx={{ mr: 1 }}
                  checked={isSelectAll ? allSelected : selected}
                  indeterminate={isSelectAll && !allSelected && selectedDevices.length > 0}
                />
                {isSelectAll ? t("energyReport.selectAll") : deviceLabel(device)}
              </li>
            );
          }}
          // A fleet is hundreds of vehicles: one chip each would fill the page while the field
          // has focus. More than a couple show as one count instead.
          renderTags={(value, getTagProps) =>
            value.length > MAX_VEHICLE_CHIPS ? (
              <Chip
                size="small"
                label={
                  allSelected
                    ? t("energyReport.allVehiclesSelected", { count: value.length })
                    : t("energyReport.vehiclesSelected", {
                        count: value.length,
                        total: allDevices.length,
                      })
                }
              />
            ) : (
              value.map((device, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} size="small" label={deviceLabel(device)} {...tagProps} />;
              })
            )
          }
          sx={{ minWidth: { sm: 320 } }}
          renderInput={params => <TextField {...params} label={t("energyReport.deviceFilter")} />}
        />
        <DateRangeSelect
          fromDate={fromDate}
          toDate={toDate}
          onChange={(from, to) => {
            // A start after the stored end arrives as [start, null]: write it as a same-day
            // range in one update instead of leaving an inverted pair for the picker to trip on.
            setRange(from, to ?? (from ? endOfDay(from) : undefined));
          }}
        />
        {activeTab === "chargeEvents" && (
          <FormControlLabel
            control={
              <Switch
                checked={mergeSessions}
                onChange={event => setMergeSessions(event.target.checked)}
              />
            }
            label={t("energyReport.mergeSessions")}
          />
        )}
        {isRunning ? (
          <Button color="error" startIcon={<StopIcon />} onClick={stop}>
            {t("energyReport.stop")}
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={start}
            disabled={
              !isReady ||
              allDevicesQuery.isPending ||
              devices.length === 0 ||
              tooManyVehicles ||
              !selectionCountIsExact
            }
          >
            {t("energyReport.start")}
          </Button>
        )}
      </Stack>

      {tooManyVehicles && (
        <Alert severity="warning">
          {selectionCountIsExact
            ? t("energyReport.tooManyVehicles", { count: devices.length, max: MAX_SCAN_VEHICLES })
            : t("energyReport.tooManyVehiclesTruncated", { max: MAX_SCAN_VEHICLES })}
        </Alert>
      )}

      {run === null && !tooManyVehicles && devices.length > 0 && (
        <Alert severity="info" role="status">
          {t("energyReport.notStarted")}
        </Alert>
      )}

      {run && isStale && (
        <Alert severity="info" role="status">
          {t("energyReport.staleRun", {
            from: formatDate(run.fromDate, i18n.language),
            to: formatDate(run.toDate, i18n.language),
          })}
        </Alert>
      )}

      {run?.stopped && report.progress.isLoading && (
        <Alert severity="info" role="status">
          {t("energyReport.stopped", { read: report.progress.read, total: report.progress.total })}
        </Alert>
      )}

      <QualitySummary counts={counts} />

      {isRunning && (
        <Box>
          <LinearProgress
            variant="determinate"
            value={
              report.progress.total > 0 ? (report.progress.read / report.progress.total) * 100 : 0
            }
          />
          <Typography variant="caption" color="text.secondary">
            {t("energyReport.loadProgress", {
              read: report.progress.read,
              total: report.progress.total,
            })}
          </Typography>
        </Box>
      )}

      {report.progress.failed > 0 && (
        <Alert severity="warning">
          {t(
            "energyReport.failedRequests",
            "Für {{count}} Fahrzeuge konnte Geotab keine Daten liefern. Die betroffenen Zeilen sind in der Spalte Datenqualität markiert.",
            { count: report.progress.failed },
          )}
          {report.error ? ` (${report.error.message})` : ""}
        </Alert>
      )}

      {allDevicesQuery.isError && (
        <Alert severity="error">
          {t("energyReport.devicesFailed")}
          {allDevicesQuery.error ? ` (${allDevicesQuery.error.message})` : ""}
        </Alert>
      )}

      {deviceListTruncated && (
        <Alert severity="warning">
          {t(
            "energyReport.devicesTruncated",
            "Es werden nur die ersten {{count}} Fahrzeuge berücksichtigt; weitere Fahrzeuge fehlen im Bericht.",
            { count: RESULTS_LIMIT_DEVICE },
          )}
        </Alert>
      )}

      {driversFailed && (
        <Alert severity="warning">
          {t("energyReport.driversFailed")}
          {driversError ? ` (${driversError.message})` : ""}
        </Alert>
      )}

      {addressError && (
        <Alert severity="warning">
          {t(
            "energyReport.addressesFailed",
            "Die Adressen konnten nicht ermittelt werden. Die Spalte Ort zeigt Koordinaten.",
          )}
          {` (${addressError.message})`}
        </Alert>
      )}

      {vinError && (
        <Alert severity="warning">
          {t(
            "energyReport.vinFailed",
            "Die Fahrzeugtypen konnten nicht aus der Fahrgestellnummer ermittelt werden.",
          )}
          {` (${vinError.message})`}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_event, value: TabKey) => setActiveTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab value="consumption" label={t("energyReport.tabs.consumption")} />
        <Tab value="chargeEvents" label={t("energyReport.tabs.chargeEvents")} />
        <Tab value="fuelUpEvents" label={t("energyReport.tabs.fuelUpEvents")} />
      </Tabs>

      <Paper sx={{ flex: 1, minHeight: 420 }}>
        {activeTab === "consumption" && (
          <EnergyReportGrid
            rows={report.consumptionRows}
            columns={consumptionColumns}
            loading={allDevicesQuery.isPending}
            report={exportReport(t("energyReport.file.consumption"))}
            fractionDigits={CONSUMPTION_FRACTION_DIGITS}
            settingsKey="energyReportConsumption"
            exportDisabled={exportDisabled}
            initialState={{
              columns: { columnVisibilityModel: CONSUMPTION_HIDDEN_COLUMNS },
              sorting: { sortModel: [{ field: "status", sort: "asc" }] },
            }}
          />
        )}
        {activeTab === "chargeEvents" && (
          <EnergyReportGrid
            rows={chargeEventRows}
            columns={chargeEventColumns}
            loading={isRunning && chargeEventRows.length === 0}
            report={exportReport(t("energyReport.file.chargeEvents"))}
            fractionDigits={CHARGE_EVENT_FRACTION_DIGITS}
            settingsKey="energyReportChargeEvents"
            exportDisabled={exportDisabled}
            initialState={{ sorting: { sortModel: [{ field: "startTime", sort: "desc" }] } }}
          />
        )}
        {activeTab === "fuelUpEvents" && (
          <EnergyReportGrid
            rows={report.fuelUpEventRows}
            columns={fuelUpEventColumns}
            loading={isRunning && report.fuelUpEventRows.length === 0}
            report={exportReport(t("energyReport.file.fuelUpEvents"))}
            fractionDigits={FUEL_UP_EVENT_FRACTION_DIGITS}
            settingsKey="energyReportFuelUps"
            exportDisabled={exportDisabled}
            initialState={{ sorting: { sortModel: [{ field: "dateTime", sort: "desc" }] } }}
          />
        )}
      </Paper>
    </Stack>
  );
}
